import { Button, HTMLSelect, Label } from "@blueprintjs/core";
import { showError, showSuccess } from "nsitools-react";
import * as React from "react";

import { AffixRequestApi, ERequestState, ERequestType, RequestInfoDto } from "../../../../api";
import { useApiService, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { KennelExportRequestDetail } from "./detail/KennelExportRequestDetail";
import { KennelImportRequestDetail } from "./detail/KennelImportRequestDetail";
import { KennelNameChangeRequestDetail } from "./detail/KennelNameChangeRequestDetail";
import { KennelOwnershipChangeRequestDetail } from "./detail/KennelOwnershipChangeRequestDetail";
import { NewKennelRequestDetail } from "./detail/NewKennelRequestDetail";

export interface IAffixRequestDetailProps {
  affixRequestId: number;
  requestInfo: RequestInfoDto;
  editMode: boolean;
}

export const AffixRequestDetail: React.FunctionComponent<IAffixRequestDetailProps> = ({
  affixRequestId,
  editMode,
  requestInfo,
}) => {
  const { t } = useTl();
  const affixRequestApi = useApiService(AffixRequestApi);

  const [currentRequestType, setCurrentRequestType] = React.useState<ERequestType>(
    requestInfo?.requestTypeCode ?? null,
  );
  const [requestTypes, rtloading] = useReferential((a) => a.getAffixRequestTypes(), false, [], true);
  const [pageLoading, setPageLoading] = React.useState<boolean>(false);

  // =========================== DATA MANIPULATION ==================================================

  const RequestPage = React.useCallback(() => {
    switch (currentRequestType) {
      case ERequestType.NewKennelRequest:
        return (
          <NewKennelRequestDetail newKennelRequestId={affixRequestId} editMode={editMode} requestInfo={requestInfo} />
        );
      case ERequestType.KennelNameChangeRequest:
        return (
          <KennelNameChangeRequestDetail
            kennelNameChangeRequestId={affixRequestId}
            editMode={editMode}
            requestInfo={requestInfo}
          />
        );
      case ERequestType.KennelImportRequest:
        return (
          <KennelImportRequestDetail
            kennelImportRequestId={affixRequestId}
            editMode={editMode}
            requestInfo={requestInfo}
          />
        );
      case ERequestType.KennelExportRequest:
        return (
          <KennelExportRequestDetail
            kennelExportRequestId={affixRequestId}
            editMode={editMode}
            requestInfo={requestInfo}
          />
        );
      case ERequestType.KennelOwnershipChangeRequest:
        return (
          <KennelOwnershipChangeRequestDetail
            kennelOwnershipChangeRequestId={affixRequestId}
            editMode={editMode}
            requestInfo={requestInfo}
          />
        );
      default:
        return <></>;
    }
  }, [affixRequestId, currentRequestType, editMode, requestInfo]);

  const handleChange = React.useCallback((event) => {
    console.log("NewType:", event.target.value);
    setCurrentRequestType(event.target.value);
  }, []);

  React.useEffect(() => {
    setPageLoading(true);
    if (!rtloading) {
      if (!requestInfo) {
        setCurrentRequestType(ERequestType.NewKennelRequest);
      } else {
        setCurrentRequestType(requestInfo.requestTypeCode);
      }
    }
    setPageLoading(false);
  }, [requestInfo, requestTypes, rtloading]);
  const [regenLoading, setregenLoading] = React.useState(false);
  const regenDoc = React.useCallback(async () => {
    setregenLoading(true);
    try {
      await affixRequestApi.regenKennelApprovedDocument(requestInfo?.parentRequestId, currentRequestType);
      showSuccess(t(ETLCodes.Success));
    } catch (e) {
      console.log(requestInfo);
      showError(t(ETLCodes.Error));
    } finally {
      setregenLoading(false);
    }
  }, [affixRequestApi, currentRequestType, requestInfo, t]);

  return (
    !pageLoading &&
    !rtloading && (
      <>
        {!requestInfo && (
          <Label style={{ display: "flex" }}>
            <div style={{ marginRight: "10px", marginTop: "10px" }}> {t(ETLCodes.AffixType)}:</div>
            {requestInfo ? (
              <div style={{ fontWeight: "bold", marginTop: "10px" }}>
                {requestTypes.find((e) => (e.value = ""))?.label}
              </div>
            ) : (
              <HTMLSelect options={requestTypes} onChange={handleChange} value={currentRequestType} />
            )}
          </Label>
        )}
        <>
          {requestInfo?.requestStateCode === ERequestState.EXECUTED && (
            <Button
              icon="refresh"
              text={t(ETLCodes.RegenKennelApprovedDocument)}
              onClick={regenDoc}
              loading={regenLoading}
              minimal
              intent="primary"
            />
          )}
          <RequestPage />
        </>
      </>
    )
  );
};
